import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type {
  ConsoleHierarchyLink,
  ConsoleHierarchyOption,
  ConsoleHierarchyRow,
} from './types';

interface RegisteredRow {
  ownerKey: string;
  row: ConsoleHierarchyRow;
}

interface HierarchyExtensionContextValue {
  rows: ConsoleHierarchyRow[];
  register: (ownerKey: string, row: ConsoleHierarchyRow | null) => void;
  unregister: (ownerKey: string) => void;
}

const HierarchyExtensionContext =
  createContext<HierarchyExtensionContextValue | null>(null);
const unavailableExtensionContext: HierarchyExtensionContextValue = {
  rows: [],
  register: () => undefined,
  unregister: () => undefined,
};

function linksEqual(
  first: ConsoleHierarchyLink | undefined,
  second: ConsoleHierarchyLink | undefined,
): boolean {
  return (
    first?.label === second?.label &&
    first?.to === second?.to &&
    first?.testId === second?.testId
  );
}

function optionsEqual(
  first: ConsoleHierarchyOption[] | undefined,
  second: ConsoleHierarchyOption[] | undefined,
): boolean {
  if (first === second) return true;
  if (!first || !second || first.length !== second.length) return false;
  return first.every(
    (option, index) =>
      option.id === second[index].id &&
      option.label === second[index].label &&
      option.to === second[index].to,
  );
}

function rowsEqual(
  first: ConsoleHierarchyRow,
  second: ConsoleHierarchyRow,
): boolean {
  return (
    first.key === second.key &&
    first.kind === second.kind &&
    first.label === second.label &&
    linksEqual(first.back, second.back) &&
    linksEqual(first.settings, second.settings) &&
    first.settingsPrefix === second.settingsPrefix &&
    optionsEqual(first.options, second.options) &&
    first.optionsLoading === second.optionsLoading &&
    first.optionsError === second.optionsError &&
    first.subtitle === second.subtitle &&
    first.extra === second.extra &&
    first.testId === second.testId &&
    first.titleTabIndex === second.titleTabIndex
  );
}

export const HierarchyExtensionProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [registeredRows, setRegisteredRows] = useState<RegisteredRow[]>([]);

  const unregister = useCallback((ownerKey: string) => {
    setRegisteredRows((current) => {
      const next = current.filter((entry) => entry.ownerKey !== ownerKey);
      return next.length === current.length ? current : next;
    });
  }, []);

  const register = useCallback(
    (ownerKey: string, row: ConsoleHierarchyRow | null) => {
      if (!row) {
        unregister(ownerKey);
        return;
      }

      setRegisteredRows((current) => {
        const existingIndex = current.findIndex(
          (entry) => entry.ownerKey === ownerKey,
        );
        if (existingIndex === -1) {
          return [...current, { ownerKey, row }];
        }
        if (rowsEqual(current[existingIndex].row, row)) {
          return current;
        }

        const next = [...current];
        next[existingIndex] = { ownerKey, row };
        return next;
      });
    },
    [unregister],
  );

  const value = useMemo(
    () => ({
      register,
      rows: registeredRows.map((entry) => entry.row),
      unregister,
    }),
    [register, registeredRows, unregister],
  );

  return (
    <HierarchyExtensionContext.Provider value={value}>
      {children}
    </HierarchyExtensionContext.Provider>
  );
};

function useHierarchyExtensionContext(): HierarchyExtensionContextValue {
  const context = useContext(HierarchyExtensionContext);
  return context ?? unavailableExtensionContext;
}

export function useHierarchyExtensions(): ConsoleHierarchyRow[] {
  return useHierarchyExtensionContext().rows;
}

export function useRegisterHierarchyRow(
  ownerKey: string,
  row: ConsoleHierarchyRow | null,
): void {
  const { register, unregister } = useHierarchyExtensionContext();

  useEffect(() => {
    register(ownerKey, row);
  }, [ownerKey, register, row]);

  useEffect(
    () => () => {
      unregister(ownerKey);
    },
    [ownerKey, unregister],
  );
}
